import React, { useEffect, useState, useMemo } from 'react';
import BaseTile from '../../../components/Tile/BaseTile';
import ContentSection from '../../../components/layout/ContentSection';
import { formatGermanNumber } from '../../../utils/numberFormat';
import { useGlobalAPI } from '../../../hooks/useGlobalAPI';

const NumberCell = ({ value, decimals = 0 }) => (
  <span className="font-medium text-gray-900">
    {typeof value === 'number'
      ? formatGermanNumber(value, { decimals, maximumFractionDigits: decimals, minimumFractionDigits: decimals })
      : '—'}
  </span>
);

// Normalize various possible backend shapes to the expected { items, validation } format
const normalizeToItems = (resp) => {
  if (!resp || typeof resp !== 'object') return { items: [], validation: null };
  // Full analytics shape already
  if (Array.isArray(resp.items)) {
    return { items: resp.items, validation: resp.validation || null };
  }
  // Simplified mapping under avg_events_per_object_type
  if (resp && typeof resp.avg_events_per_object_type === 'object' && resp.avg_events_per_object_type !== null) {
    const items = Object.entries(resp.avg_events_per_object_type).map(([type, avg]) => ({
      object_type: String(type),
      total_objects: 0,
      total_references: 0,
      avg_events_per_object: Number(avg) || 0
    }));
    items.sort((a, b) => (b.avg_events_per_object - a.avg_events_per_object) || a.object_type.localeCompare(b.object_type));
    return { items, validation: resp.validation || null };
  }
  // Alternate mapping under averages
  if (resp && typeof resp.averages === 'object' && resp.averages !== null) {
    const items = Object.entries(resp.averages).map(([type, avg]) => ({
      object_type: String(type),
      total_objects: 0,
      total_references: 0,
      avg_events_per_object: Number(avg) || 0
    }));
    items.sort((a, b) => (b.avg_events_per_object - a.avg_events_per_object) || a.object_type.localeCompare(b.object_type));
    return { items, validation: resp.validation || null };
  }
  // Plain mapping fallback
  const numericEntries = Object.entries(resp).filter(([k, v]) => typeof v === 'number');
  if (numericEntries.length > 0) {
    const items = numericEntries.map(([type, avg]) => ({
      object_type: String(type),
      total_objects: 0,
      total_references: 0,
      avg_events_per_object: Number(avg) || 0
    }));
    items.sort((a, b) => (b.avg_events_per_object - a.avg_events_per_object) || a.object_type.localeCompare(b.object_type));
    return { items, validation: null };
  }
  return { items: [], validation: resp.validation || null };
};

/**
 * Tile: Average events per object by object type
 */
const EventIntensityPerObjectTypeTile = ({ activeFile, topN = 8 }) => {
  const api = useGlobalAPI(activeFile);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!activeFile) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.post('/kpi/object-type-event-intensity', {});
        if (!cancelled) setRawData(res);
      } catch (e) {
        if (!cancelled) {
          const msg = String(e?.message || e || '');
          if (/Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg)) {
            setRawData({ file_type: activeFile || 'main', items: [], validation: { consistency_ok: true, note: 'Endpoint not available' } });
            setError(null);
          } else {
            setError(msg || 'Failed to load event intensity');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [api.currentFilters, activeFile]);

  const { items: allItems, validation } = useMemo(() => normalizeToItems(rawData), [rawData]);

  const items = useMemo(() => {
    const arr = [...allItems];
    arr.sort((a, b) => (b.avg_events_per_object - a.avg_events_per_object) || a.object_type.localeCompare(b.object_type));
    if (arr.length <= topN + 2) return arr;
    return arr.slice(0, topN);
  }, [allItems, topN]);

  return (
    <ContentSection spacing="lg">
      <BaseTile
        title="Avg Events per Object by Object Type"
        loading={loading}
        error={error}
        minHeight="md"
      >
        <div className="p-4">
          {items.length === 0 && !loading && !error ? (
            <div className="text-sm text-gray-600 text-center py-6">No data available for the current dataset/configuration.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Object Type</th>
                    <th className="py-2 pr-4 text-right">Objects</th>
                    <th className="py-2 pr-4 text-right">References</th>
                    <th className="py-2 pr-2 text-right">Avg Events / Obj</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.object_type} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-gray-800 truncate" title={it.object_type}>{it.object_type}</td>
                      <td className="py-2 pr-4 text-right"><NumberCell value={it.total_objects} /></td>
                      <td className="py-2 pr-4 text-right"><NumberCell value={it.total_references} /></td>
                      <td className="py-2 pr-2 text-right"><NumberCell value={it.avg_events_per_object} decimals={2} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {validation && (
            <div className="mt-3 text-xs text-gray-500">
              <div>
                Validation: {validation.consistency_ok ? 'OK' : 'Issues detected'}
              </div>
              {!validation.consistency_ok && (
                <div>
                  Missing object IDs referenced by events: {validation.missing_object_ids}
                </div>
              )}
            </div>
          )}
        </div>
      </BaseTile>
    </ContentSection>
  );
};

export default EventIntensityPerObjectTypeTile;
